import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { feedbackService, FeedbackAnalyticsResult } from '../../services/feedback/feedbackService';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';

const FeedbackAnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<FeedbackAnalyticsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatformAdmin = location.pathname.startsWith('/platform-admin');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feedbackService.getAnalytics();
      setData(res);
    } catch (error: any) {
      message.error(error.message || '获取数据看板失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolved = data?.byStatus.find((s) => s._id === 'resolved')?.count ?? 0;
  const pending = data?.byStatus.find((s) => s._id === 'pending')?.count ?? 0;

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        {isPlatformAdmin ? (
          <>
            <span>平台管理</span>
            <span className="mm-crumb-sep">/</span>
            <span>匿名反馈治理</span>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">数据看板</span>
          </>
        ) : (
          <>
            <Link to="/feedback/square" className="fb-crumb-link">
              畅所欲言
            </Link>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">数据看板</span>
          </>
        )}
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">匿名反馈数据看板</h1>
          <p className="mm-subtitle mb-0">本周趋势与类型分布，辅助运营与响应。</p>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : data ? (
        <>
          <section className="mm-stats mm-stats--quad" aria-label="看板指标">
            <article className="mm-stat">
              <span className="mm-stat-label">本周反馈数</span>
              <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {data.totalThisWeek}
              </span>
              <div className="mm-stat-foot">
                <LimsNavIcon name="calendar_month" sizePx={18} />
                <span>滚动周期统计</span>
              </div>
              <LimsNavIcon name="date_range" sizePx={120} className="mm-stat-deco" />
            </article>
            <article className="mm-stat">
              <span className="mm-stat-label">平均响应（小时）</span>
              <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {data.averageResponseHours != null ? data.averageResponseHours.toFixed(1) : '—'}
              </span>
              <div className="mm-stat-foot">
                <LimsNavIcon name="timer" sizePx={18} />
                <span>受理到首次跟进</span>
              </div>
              <LimsNavIcon name="speed" sizePx={120} className="mm-stat-deco" />
            </article>
            <article className="mm-stat">
              <span className="mm-stat-label">已解决</span>
              <span className="mm-stat-value">{resolved}</span>
              <div className="mm-stat-foot">
                <LimsNavIcon name="task_alt" sizePx={18} />
                <span>状态分布中 resolved</span>
              </div>
              <LimsNavIcon name="check_circle" sizePx={120} className="mm-stat-deco" />
            </article>
            <article className="mm-stat">
              <span className="mm-stat-label">待处理</span>
              <span className={`mm-stat-value${pending > 0 ? ' mm-stat-value--alert' : ''}`}>{pending}</span>
              <div className="mm-stat-foot">
                <LimsNavIcon name="pending_actions" sizePx={18} />
                <span>状态分布中 pending</span>
              </div>
              <LimsNavIcon name="inbox" sizePx={120} className="mm-stat-deco" />
            </article>
          </section>

          <div className="fb-detail-panel">
            <div className="fb-analytics-split">
              <div>
                <h2 className="fb-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                  按类型分布（本周）
                </h2>
                <ul className="fb-list-plain">
                  {data.byCategory.map((item) => (
                    <li key={item._id || 'unknown'}>
                      <span className="fb-cat fb-cat--default">{item._id || '未分类'}</span>
                      <span className="ms-auto" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.count} 条
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="fb-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                  最受关注（赞同）
                </h2>
                <ul className="fb-list-plain">
                  {data.mostLikedPosts.map((item) => (
                    <li key={item._id}>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-start text-decoration-none fb-title-link-btn"
                        onClick={() => navigate(`/feedback/post/${item._id}`)}
                      >
                        {item.title}
                      </button>
                      <span className="ms-auto text-muted small" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.likeCount} 赞同
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-muted text-center py-5">暂无数据</div>
      )}
    </div>
  );
};

export default FeedbackAnalyticsDashboard;
